import React, { useEffect, useState } from 'react';
import { adminApi } from '../utils/api';
import { Card, Table, TR, TD, RoleBadge, StatusBadge, Btn, Input, Select, Modal, PageHeader, fmtDate, fmtDateTime } from '../components/UI';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import RocketLoader from '../components/RocketLoader';

const CreateAdminModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!form.name || !form.email || !form.password) return setError('All fields required');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    setLoading(true);
    try {
      const res = await adminApi.createAdmin(form);
      onCreate(res.user);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Create New Admin" onClose={onClose}>
      {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}
      <Input label="Full Name"     value={form.name}     onChange={v => setForm(p => ({ ...p, name: v }))}     placeholder="John Doe" />
      <Input label="Email Address" value={form.email}    onChange={v => setForm(p => ({ ...p, email: v }))}    placeholder="admin@example.com" type="email" />
      <Input label="Password"      value={form.password} onChange={v => setForm(p => ({ ...p, password: v }))} placeholder="Min 8 characters" type="password" />
      <Select label="Role" value={form.role} onChange={v => setForm(p => ({ ...p, role: v }))}
        options={[['admin','Admin — Can manage users & content'],['superadmin','Super Admin — Full access']]} />
      <div style={{ background: '#F8FAFC', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#64748B' }}>
        <strong style={{ color: '#64748B' }}>Admin:</strong> Can manage users, templates, announcements<br />
        <strong style={{ color: '#64748B' }}>Super Admin:</strong> Full access including creating/removing admins and deleting users
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn color="ghost" onClick={onClose}>Cancel</Btn>
        <Btn color="blue" onClick={submit} disabled={loading}>{loading ? 'Creating...' : 'Create Admin'}</Btn>
      </div>
    </Modal>
  );
};

export const AdminsPage = () => {
  const { admin: currentAdmin } = useAdminAuth();
  const [admins, setAdmins]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    adminApi.getAdmins().then(setAdmins).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleCreate = (newAdmin) => setAdmins(prev => [newAdmin, ...prev]);

  const changeRole = async (id, role) => {
    if (id === currentAdmin?._id) return alert("You can't change your own role");
    if (!window.confirm(`Change this admin's role to ${role}?`)) return;
    try {
      const res = await adminApi.updateAdminRole(id, role);
      setAdmins(prev => prev.map(a => a._id === id ? res.user : a));
    } catch (e) { alert(e.message); }
  };

  const removeAdmin = async (id) => {
    if (id === currentAdmin?._id) return alert("You can't remove yourself");
    if (!window.confirm('Remove admin privileges from this user?')) return;
    try {
      await adminApi.removeAdmin(id);
      setAdmins(prev => prev.filter(a => a._id !== id));
    } catch (e) { alert(e.message); }
  };

  return (
    <div>
      <PageHeader
        title="Admin Management"
        subtitle="Manage admin accounts and permissions (Super Admin only)"
        action={<Btn color="blue" onClick={() => setShowCreate(true)}>+ Create Admin</Btn>}
      />

      <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#B45309' }}>
        ⚠️ This section is restricted to Super Admins only. Changes here affect who can access this admin panel.
      </div>

      <Card style={{ padding: 0 }}>
        <Table headers={['Admin', 'Role', 'Status', 'Created', 'Last Login', 'Login Count', 'Actions']}>
          {loading ? (
            <TR><TD><div style={{ padding: 30, display: 'flex', justifyContent: 'center' }}><RocketLoader size={120} message="Loading admins…" /></div></TD></TR>
          ) : admins.map(admin => (
            <TR key={admin._id}>
              <TD>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {admin.avatar
                    ? <img src={admin.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                    : <div style={{ width: 36, height: 36, borderRadius: '50%', background: admin.role === 'superadmin' ? 'linear-gradient(135deg,#DC2626,#FB923C)' : 'linear-gradient(135deg,#EF1D28,#F97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 14 }}>{admin.name?.charAt(0)}</div>}
                  <div>
                    <div style={{ color: '#0F172A', fontWeight: 600, fontSize: 13 }}>
                      {admin.name}
                      {admin._id === currentAdmin?._id && <span style={{ color: '#15803D', fontSize: 10, marginLeft: 6 }}>(You)</span>}
                    </div>
                    <div style={{ color: '#64748B', fontSize: 11 }}>{admin.email}</div>
                  </div>
                </div>
              </TD>
              <TD><RoleBadge role={admin.role} /></TD>
              <TD><StatusBadge status={admin.status || 'active'} /></TD>
              <TD style={{ color: '#64748B', fontSize: 12 }}>{fmtDate(admin.createdAt)}</TD>
              <TD style={{ color: '#64748B', fontSize: 12 }}>{fmtDateTime(admin.lastLogin)}</TD>
              <TD style={{ color: '#64748B' }}>{admin.loginCount || 0}</TD>
              <TD>
                {admin._id !== currentAdmin?._id && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    {admin.role === 'admin' ? (
                      <Btn size="sm" color="yellow" onClick={() => changeRole(admin._id, 'superadmin')}>↑ Promote</Btn>
                    ) : (
                      <Btn size="sm" color="ghost" onClick={() => changeRole(admin._id, 'admin')}>↓ Demote</Btn>
                    )}
                    <Btn size="sm" color="red" onClick={() => removeAdmin(admin._id)}>Remove</Btn>
                  </div>
                )}
              </TD>
            </TR>
          ))}
        </Table>
      </Card>

      {showCreate && <CreateAdminModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
    </div>
  );
};
